import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';

function premiumOf(row) {
    return Number(row.premiumAmountGMD || row.premiumAmount || 0);
}

function statusOf(row) {
    return String(row.status || '').toLowerCase();
}

function paymentStatusOf(row) {
    return String(row.paymentStatus || row.payment_status || '').toLowerCase();
}

function coverTypeOf(row) {
    const raw = String(row.insuranceType || row.policyType || row.coverageType || 'other').toLowerCase();
    if (raw.includes('motor') || raw.includes('mtr') || raw.includes('comprehensive') || raw.includes('third')) return 'motor';
    if (raw.includes('med') || raw.includes('family') || raw.includes('individual')) return 'medical';
    if (raw.includes('property') || raw.includes('fire') || raw.includes('home') || raw.includes('shop')) return 'property';
    return 'other';
}

function toDate(value) {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function periodStart(period) {
    const now = new Date();
    if (period === 'today') return startOfDay(now);
    if (period === 'week') {
        const d = startOfDay(now);
        d.setDate(d.getDate() - d.getDay());
        return d;
    }
    if (period === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
    if (period === 'quarter') return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    if (period === 'year') return new Date(now.getFullYear(), 0, 1);
    return new Date(2000, 0, 1);
}

function money(n) {
    return 'D ' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusBadge(status) {
    if (status === 'active') return 'bd-badge bd-badge-active';
    if (status === 'pending' || status === 'quote') return 'bd-badge bd-badge-pending';
    return 'bd-badge bd-badge-inactive';
}

function catClass(type) {
    if (type === 'motor') return 'bd-cat bd-cat-motor';
    if (type === 'medical') return 'bd-cat bd-cat-medical';
    if (type === 'property') return 'bd-cat bd-cat-property';
    return 'bd-cat bd-cat-other';
}

function Collapsible({ title, countLabel, defaultOpen, children }) {
    const [open, setOpen] = useState(!!defaultOpen);
    return (
        <div className="bd-card">
            <div className="bd-collapsible-header" onClick={() => setOpen(!open)}>
                <div className="bd-header-left">
                    <span>{title}</span>
                    <span className="bd-count-pill">{countLabel}</span>
                </div>
                <span className={'bd-arrow' + (open ? ' open' : '')}>▼</span>
            </div>
            {open && <div className="bd-collapsible-body">{children}</div>}
        </div>
    );
}

function RoleBasedDashboard() {
    const { session, loading } = useCompanySession();
    const [period, setPeriod] = useState('week');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [rows, setRows] = useState({
        policies: [],
        claims: [],
        customers: [],
        transactions: [],
        brokers: 0
    });
    const [ready, setReady] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(new Date());

    const load = () => {
        if (!session?.tenantId) return;
        const tenantId = session.tenantId;
        const brokerOnly = session.role === 'broker';
        setReady(false);
        Promise.all([
            getDocs(collection(db, 'tenants', tenantId, 'policies')),
            getDocs(collection(db, 'tenants', tenantId, 'claims')),
            getDocs(collection(db, 'tenants', tenantId, 'customers')),
            getDocs(collection(db, 'tenants', tenantId, 'transactions')),
            getDocs(collection(db, 'tenants', tenantId, 'users'))
        ]).then(([pSnap, cSnap, cuSnap, tSnap, uSnap]) => {
            const policies = [];
            pSnap.forEach((d) => {
                const data = { id: d.id, ...d.data() };
                if (brokerOnly && data.brokerId && data.brokerId !== session.uid) return;
                policies.push(data);
            });
            const customers = [];
            cuSnap.forEach((d) => {
                const data = { id: d.id, ...d.data() };
                if (brokerOnly && data.brokerId && data.brokerId !== session.uid) return;
                customers.push(data);
            });
            const claims = [];
            cSnap.forEach((d) => claims.push({ id: d.id, ...d.data() }));
            const transactions = [];
            tSnap.forEach((d) => transactions.push({ id: d.id, ...d.data() }));
            let brokers = 0;
            uSnap.forEach((d) => {
                if (String(d.data().role || '').toLowerCase() === 'broker') brokers += 1;
            });
            setRows({ policies, claims, customers, transactions, brokers });
            setUpdatedAt(new Date());
            setReady(true);
        }).catch((err) => {
            console.error(err);
            setReady(true);
        });
    };

    useEffect(() => {
        if (session?.tenantId) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    const stats = useMemo(() => {
        const now = new Date();
        const today = startOfDay(now);
        const week = periodStart('week');
        const month = periodStart('month');
        const lastWeek = new Date(week);
        lastWeek.setDate(lastWeek.getDate() - 7);
        const filterStart = periodStart(period);
        const soon = new Date();
        soon.setDate(soon.getDate() + 30);

        let todayPremium = 0;
        let todayCount = 0;
        let weekPremium = 0;
        let lastWeekPremium = 0;
        let monthPremium = 0;
        let periodPremium = 0;
        let totalPremium = 0;
        let paidPremium = 0;
        let outstanding = 0;
        let outstandingCount = 0;
        let activePolicies = 0;
        let pendingApprovals = 0;
        const byCover = { motor: 0, medical: 0, property: 0, other: 0 };
        const byChannel = {};
        const dayBuckets = {};
        for (let i = 6; i >= 0; i -= 1) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            dayBuckets[d.toISOString().slice(0, 10)] = 0;
        }

        rows.policies.forEach((policy) => {
            const amount = premiumOf(policy);
            const created = toDate(policy.createdAt) || toDate(policy.effectiveDate) || toDate(policy.expiryDate);
            const status = statusOf(policy);
            const paid = paymentStatusOf(policy) === 'paid' || status === 'active';
            totalPremium += amount;
            if (status === 'active') activePolicies += 1;
            if (status === 'pending') pendingApprovals += 1;
            if (paid) paidPremium += amount;
            else {
                outstanding += amount;
                outstandingCount += 1;
            }
            byCover[coverTypeOf(policy)] += amount;
            if (created) {
                const key = created.toISOString().slice(0, 10);
                if (dayBuckets[key] !== undefined) dayBuckets[key] += amount;
                if (created >= today) { todayPremium += amount; todayCount += 1; }
                if (created >= week) weekPremium += amount;
                if (created >= lastWeek && created < week) lastWeekPremium += amount;
                if (created >= month) monthPremium += amount;
                if (created >= filterStart) periodPremium += amount;
            }
        });

        rows.transactions.forEach((tx) => {
            const amount = Number(tx.amountPaidGMD || tx.amount || 0);
            const channel = String(tx.paymentMethod || tx.channel || 'OTHER').toUpperCase();
            if (String(tx.status || '').toUpperCase() === 'SUCCESSFUL') {
                byChannel[channel] = (byChannel[channel] || 0) + amount;
            }
        });

        const expiring = rows.policies
            .map((p) => ({ ...p, expiry: toDate(p.expiryDate) }))
            .filter((p) => p.expiry && p.expiry <= soon && statusOf(p) === 'active')
            .sort((a, b) => a.expiry - b.expiry);

        const weekChange = lastWeekPremium === 0 ? (weekPremium > 0 ? 100 : 0) : ((weekPremium - lastWeekPremium) / lastWeekPremium) * 100;
        const collectionRate = totalPremium === 0 ? 0 : (paidPremium / totalPremium) * 100;
        const avgPremium = rows.policies.length ? totalPremium / rows.policies.length : 0;
        const topCover = Object.entries(byCover).sort((a, b) => b[1] - a[1])[0] || ['-', 0];
        const topCoverPct = totalPremium === 0 ? 0 : (topCover[1] / totalPremium) * 100;
        const monthProjection = monthPremium * (new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() / Math.max(now.getDate(), 1));

        return {
            todayPremium,
            todayCount,
            weekPremium,
            weekChange,
            monthPremium,
            monthProjection,
            periodPremium,
            totalPremium,
            paidPremium,
            outstanding,
            outstandingCount,
            activePolicies,
            pendingApprovals,
            collectionRate,
            avgPremium,
            topCover: topCover[0],
            topCoverPct,
            expiring,
            byCover,
            byChannel,
            trend: Object.keys(dayBuckets).map((key) => ({
                label: key.slice(5).replace('-', '/'),
                value: dayBuckets[key]
            }))
        };
    }, [rows, period]);

    const filteredPolicies = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.policies.filter((p) => {
            const status = statusOf(p);
            if (statusFilter !== 'all' && status !== statusFilter) return false;
            if (!q) return true;
            const hay = [
                p.policyNumber,
                p.policyHolder?.fullName,
                p.fullName,
                p.policyHolder?.phone,
                p.phone
            ].join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [rows.policies, search, statusFilter]);

    const exportCsv = () => {
        const header = 'Policy,Client,Type,Status,Premium,Expiry\n';
        const body = rows.policies.map((p) => [
            p.policyNumber || p.id,
            (p.policyHolder?.fullName || p.fullName || '').replace(/,/g, ' '),
            coverTypeOf(p),
            p.status || '',
            premiumOf(p),
            toDate(p.expiryDate) ? toDate(p.expiryDate).toLocaleDateString() : ''
        ].join(',')).join('\n');
        const blob = new Blob([header + body], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pulse-insurance-policies.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading || !session) {
        return (
            <div className="bd-app">
                <div className="bd-container" style={{ paddingTop: 80, textAlign: 'center', color: '#64748b' }}>
                    <div className="bd-spinner" /> Loading dashboard...
                </div>
            </div>
        );
    }

    const maxBar = Math.max(...stats.trend.map((p) => p.value), 1);
    const channelTotal = Object.values(stats.byChannel).reduce((s, n) => s + n, 0) || 1;
    const coverTotal = Object.values(stats.byCover).reduce((s, n) => s + n, 0) || 1;

    return (
        <CompanyLayout session={session} hidePageTitle>
            <div className="bd-quick-actions" style={{ marginTop: -8 }}>
                <span className="bd-label">⚡ Tools:</span>
                <button type="button" className="bd-btn bd-btn-info bd-btn-sm" onClick={load}>🔄 Refresh</button>
                <button type="button" className="bd-btn bd-btn-warning bd-btn-sm" onClick={exportCsv}>📄 Export CSV</button>
                <Link to="/my-policy" className="bd-btn bd-btn-secondary bd-btn-sm">📱 Client lookup</Link>
            </div>

            {!ready && (
                <div className="bd-card" style={{ textAlign: 'center', color: '#64748b' }}>
                    <div className="bd-spinner" /> Loading live stats...
                </div>
            )}

            <div className="bd-live-stats">
                <div className="bd-stat-card">
                    <div className="bd-label">📊 Today's Premium</div>
                    <div className="bd-value green">{money(stats.todayPremium)}</div>
                    <div className="bd-sub">{stats.todayCount} policies written today</div>
                </div>
                <div className="bd-stat-card">
                    <div className="bd-label">📈 This Week</div>
                    <div className="bd-value blue">{money(stats.weekPremium)}</div>
                    <div className="bd-sub">{stats.weekChange >= 0 ? '↑' : '↓'} {Math.abs(stats.weekChange).toFixed(0)}% vs last week</div>
                </div>
                <div className="bd-stat-card">
                    <div className="bd-label">📅 This Month</div>
                    <div className="bd-value purple">{money(stats.monthPremium)}</div>
                    <div className="bd-sub">Projected: {money(stats.monthProjection)}</div>
                </div>
                <div className="bd-stat-card">
                    <div className="bd-label">👤 Active Policies</div>
                    <div className="bd-value orange">{stats.activePolicies}</div>
                    <div className="bd-sub">{rows.customers.length} clients · {rows.brokers} brokers</div>
                </div>
                <div className="bd-stat-card">
                    <div className="bd-label">💰 Outstanding Premium</div>
                    <div className="bd-value red">{money(stats.outstanding)}</div>
                    <div className="bd-sub">{stats.outstandingCount} unpaid / pending files</div>
                </div>
            </div>

            <div className="bd-exec">
                <div className="bd-exec-header">
                    <h2>📊 Executive Dashboard</h2>
                    <span className="bd-exec-updated">Updated: {updatedAt.toLocaleTimeString()}</span>
                </div>
                <div className="bd-exec-grid">
                    <div className="bd-exec-card">
                        <div className="bd-exec-icon">💰</div>
                        <div className="bd-exec-value">{money(stats.totalPremium)}</div>
                        <div className="bd-exec-label">Gross Written Premium</div>
                        <div className={'bd-exec-trend ' + (stats.weekChange >= 0 ? 'up' : 'down')}>
                            {stats.weekChange >= 0 ? '↑' : '↓'} {Math.abs(stats.weekChange).toFixed(0)}%
                        </div>
                    </div>
                    <div className="bd-exec-card">
                        <div className="bd-exec-icon">✅</div>
                        <div className="bd-exec-value">{stats.collectionRate.toFixed(0)}%</div>
                        <div className="bd-exec-label">Collection Rate</div>
                        <div className="bd-exec-trend up">Paid {money(stats.paidPremium)}</div>
                    </div>
                    <div className="bd-exec-card">
                        <div className="bd-exec-icon">👤</div>
                        <div className="bd-exec-value">{rows.customers.length}</div>
                        <div className="bd-exec-label">Clients</div>
                        <div className="bd-exec-trend up">Pending: {stats.pendingApprovals}</div>
                    </div>
                    <div className="bd-exec-card">
                        <div className="bd-exec-icon">📋</div>
                        <div className="bd-exec-value">{money(stats.outstanding)}</div>
                        <div className="bd-exec-label">Outstanding Balance</div>
                        <div className="bd-exec-trend down">Due: {stats.outstandingCount}</div>
                    </div>
                </div>
            </div>

            <div className="bd-card">
                <h3>📊 Advanced Analytics <span className="bd-badge-count">Last 30 days</span></h3>
                <div className="bd-analytics-grid">
                    <div className="bd-analytics-card">
                        <div className="bd-label">Collection Rate</div>
                        <div className="bd-value">{stats.collectionRate.toFixed(0)}%</div>
                        <div className="bd-trend up">Paid vs written</div>
                    </div>
                    <div className="bd-analytics-card">
                        <div className="bd-label">Avg Premium</div>
                        <div className="bd-value">{money(stats.avgPremium)}</div>
                        <div className="bd-trend up">Per policy</div>
                    </div>
                    <div className="bd-analytics-card">
                        <div className="bd-label">Open Claims</div>
                        <div className="bd-value">{rows.claims.length}</div>
                        <div className="bd-trend">{rows.claims.length ? 'In progress' : 'None open'}</div>
                    </div>
                    <div className="bd-analytics-card">
                        <div className="bd-label">Top Line</div>
                        <div className="bd-value" style={{ textTransform: 'capitalize' }}>{stats.topCover}</div>
                        <div className="bd-trend">{stats.topCoverPct.toFixed(0)}% of GWP</div>
                    </div>
                </div>
            </div>

            <div className="bd-card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>📅 Premium Period:</span>
                    <div className="bd-date-filter">
                        {[
                            ['today', 'Today'],
                            ['week', 'This Week'],
                            ['month', 'This Month'],
                            ['quarter', 'This Quarter'],
                            ['year', 'This Year'],
                            ['all', 'All Time']
                        ].map(([item, label]) => (
                            <button
                                key={item}
                                type="button"
                                className={'bd-filter-btn' + (period === item ? ' active' : '')}
                                onClick={() => setPeriod(item)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
                    Written in this period: <strong style={{ color: '#0f172a' }}>{money(stats.periodPremium)}</strong>
                </div>
            </div>

            <Collapsible title="💰 Outstanding Premium" countLabel={stats.outstandingCount + ' files'} defaultOpen>
                <div style={{ overflowX: 'auto' }}>
                    <table className="bd-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Policy</th>
                                <th>Line</th>
                                <th>Premium</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.policies.filter((p) => paymentStatusOf(p) !== 'paid' && statusOf(p) !== 'active').slice(0, 20).map((p) => (
                                <tr key={p.id}>
                                    <td>{p.policyHolder?.fullName || p.fullName || '—'}</td>
                                    <td>{p.policyNumber || p.id}</td>
                                    <td><span className={catClass(coverTypeOf(p))}>{coverTypeOf(p)}</span></td>
                                    <td>{money(premiumOf(p))}</td>
                                    <td><span className={statusBadge(statusOf(p))}>{p.status || 'pending'}</span></td>
                                </tr>
                            ))}
                            {stats.outstandingCount === 0 && (
                                <tr><td colSpan="5" className="bd-no-data">No outstanding premium</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Collapsible>

            <Collapsible title="⚠️ Expiring Soon" countLabel={stats.expiring.length + ' policies'} defaultOpen>
                <div style={{ overflowX: 'auto' }}>
                    <table className="bd-table">
                        <thead>
                            <tr>
                                <th>Policy</th>
                                <th>Client</th>
                                <th>Expiry</th>
                                <th>Premium</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.expiring.slice(0, 12).map((p) => (
                                <tr key={p.id}>
                                    <td>{p.policyNumber || p.id}</td>
                                    <td>{p.policyHolder?.fullName || p.fullName || '—'}</td>
                                    <td>{p.expiry ? p.expiry.toLocaleDateString() : '—'}</td>
                                    <td>{money(premiumOf(p))}</td>
                                </tr>
                            ))}
                            {stats.expiring.length === 0 && (
                                <tr><td colSpan="4" className="bd-no-data">No policies expiring in the next 30 days</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Collapsible>

            <Collapsible title="👥 Clients" countLabel={String(rows.customers.length)}>
                <div style={{ overflowX: 'auto' }}>
                    <table className="bd-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Phone</th>
                                <th>National ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.customers.slice(0, 20).map((c) => (
                                <tr key={c.id}>
                                    <td>{c.fullName || '—'}</td>
                                    <td>{c.phone || '—'}</td>
                                    <td>{c.nationalId || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Collapsible>

            <div className="bd-grid">
                <div className="bd-card">
                    <h3>📈 Premium Trend <span className="bd-badge-count">Last 7 days</span></h3>
                    <div className="bd-chart">
                        {stats.trend.map((point) => (
                            <div className="bd-bar-col" key={point.label}>
                                <div className="bd-bar" style={{ height: Math.max(4, (point.value / maxBar) * 180) }} title={money(point.value)} />
                                <span>{point.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bd-card">
                    <h3>💳 Mix & Channels</h3>
                    {Object.keys(stats.byCover).map((key) => (
                        <div key={key} style={{ marginBottom: 10 }}>
                            <div className="bd-channel-row">
                                <span style={{ textTransform: 'capitalize' }}>{key}</span>
                                <strong>{money(stats.byCover[key])}</strong>
                            </div>
                            <div className="bd-channel-bar">
                                <div className="bd-channel-fill" style={{ width: ((stats.byCover[key] / coverTotal) * 100) + '%' }} />
                            </div>
                        </div>
                    ))}
                    {Object.keys(stats.byChannel).length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            {Object.entries(stats.byChannel).map(([key, value]) => (
                                <div key={key} className="bd-channel-row">
                                    <span>{key}</span>
                                    <span>{money(value)} ({Math.round((value / channelTotal) * 100)}%)</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Collapsible title="📋 Policies" countLabel={filteredPolicies.length + ' of ' + rows.policies.length} defaultOpen>
                <div className="bd-search">
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="🔍 Search by policy, client or phone..."
                    />
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="quote">Quote</option>
                        <option value="expired">Expired</option>
                    </select>
                    <button type="button" className="bd-btn bd-btn-outline bd-btn-sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear Filters</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table className="bd-table">
                        <thead>
                            <tr>
                                <th>Policy</th>
                                <th>Client</th>
                                <th>Line</th>
                                <th>Premium</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPolicies.slice(0, 25).map((p) => (
                                <tr key={p.id}>
                                    <td>{p.policyNumber || p.id}</td>
                                    <td>{p.policyHolder?.fullName || p.fullName || '—'}</td>
                                    <td><span className={catClass(coverTypeOf(p))}>{coverTypeOf(p)}</span></td>
                                    <td>{money(premiumOf(p))}</td>
                                    <td><span className={statusBadge(statusOf(p))}>{p.status || 'pending'}</span></td>
                                </tr>
                            ))}
                            {filteredPolicies.length === 0 && (
                                <tr><td colSpan="5" className="bd-no-data">No policies match this filter</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Collapsible>
        </CompanyLayout>
    );
}

export default RoleBasedDashboard;
